import { DraftConfiguration, DraftState, PrintType } from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { getAvailablePrintTypes } from '@/model/configuration/getAvailablePrintTypes';
import { getCommonPrintTypes } from '@/model/configuration/getCommonPrintTypes';
import { getAppearance } from '@/model/draft/getAppearance';
import { getConfigurationsForPrintTypeAlignment } from '@/model/draft/getConfigurationsForPrintTypeAlignment';
import { isDigital } from '@/model/printType/isDigital';
import { DraftWarning } from '../../types';
import { changePrintType } from './changePrintType';
import { removeConfiguration } from './removeConfiguration';

const setPrintTypeOfConfiguration = (configuration: DraftConfiguration, printType: PrintType) => {
  if (configuration.printType.id !== printType.id) {
    changePrintType(configuration, printType);
  }
};

export const equalizeOnAdd = (draft: DraftState, newConfiguration: DraftConfiguration) => {
  // ignore the text size of a new configuration so the user can still change the text
  //  even if the current content is too big for the print area
  const availablePrintTypes = getAvailablePrintTypes(newConfiguration, getAppearance(draft), true);
  if (!availablePrintTypes.length) {
    return removeConfiguration(draft, newConfiguration.cid);
  }

  const [otherConfigurations, includesOtherSleeve] = getConfigurationsForPrintTypeAlignment(
    draft,
    newConfiguration
  );

  if (otherConfigurations.length === 0) {
    // nothing to equalize, choose the best print type for new config
    setPrintTypeOfConfiguration(newConfiguration, availablePrintTypes[0]);
  } else if (availablePrintTypes.map((p) => p.id).includes(otherConfigurations[0].printType.id)) {
    // new config can be printed with existing print type
    const otherPrintType = otherConfigurations[0].printType;

    if (isDigital(otherPrintType)) {
      // only one digital print type per print area
      setPrintTypeOfConfiguration(newConfiguration, otherPrintType);
    } else if (isDigital(newConfiguration.printType)) {
      // do not mix digital and foil, choose the best foil print type
      const printType = assertDefined(
        availablePrintTypes.find((printType) => !isDigital(printType))
      );
      setPrintTypeOfConfiguration(newConfiguration, printType);
    }
  } else {
    // all configurations on the print area have to be switched to another type
    // find the first print type that is available for every configuration
    const [newPrintType] = getCommonPrintTypes(
      [newConfiguration, ...otherConfigurations],
      getAppearance(draft)
    );

    // do nothing if there is no common print type and accept mixed print types
    //  a known case where this is the expected behavior is embroidery
    //  where designs use are configured with a digital print type and text with a vector print type
    if (newPrintType) {
      otherConfigurations.forEach((configuration) => {
        setPrintTypeOfConfiguration(configuration, newPrintType);
      });
      setPrintTypeOfConfiguration(newConfiguration, newPrintType);
      draft.warnings[newConfiguration.cid] = includesOtherSleeve
        ? DraftWarning.EqualizedSleevesOnAdd
        : DraftWarning.EqualizedOnAdd;
    }
  }
};
